import React from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  Avatar,
  Box,
} from "@material-ui/core";
import { PhotoCamera } from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import { formatMessage } from "@openimis/fe-core";

const styles = (theme) => ({
  sectionCard: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  field: {
    marginBottom: theme.spacing(1),
  },
});

// Schema-based field definitions for Insuree
const INSUREE_FIELDS = [
  {
    key: "chfId",
    label: "enrollment.InsureeEnrollment.chfId",
    type: "text",
    required: true,
  },
  {
    key: "lastName",
    label: "enrollment.InsureeEnrollment.lastName",
    type: "text",
    required: true,
  },
  {
    key: "otherNames",
    label: "enrollment.InsureeEnrollment.otherNames",
    type: "text",
    required: true,
  },
  {
    key: "dob",
    label: "enrollment.InsureeEnrollment.dob",
    type: "date",
    required: true,
  },
  {
    key: "gender",
    label: "enrollment.InsureeEnrollment.gender",
    type: "select",
    required: true,
    options: [
      { value: "M", label: "enrollment.gender.male" },
      { value: "F", label: "enrollment.gender.female" },
      { value: "O", label: "enrollment.gender.other" },
    ],
  },
  {
    key: "marital",
    label: "enrollment.InsureeEnrollment.marital",
    type: "select",
    options: [
      { value: "S", label: "enrollment.marital.single" },
      { value: "M", label: "enrollment.marital.married" },
      { value: "D", label: "enrollment.marital.divorced" },
      { value: "W", label: "enrollment.marital.widowed" },
      { value: "N", label: "enrollment.marital.not_specified" },
    ],
  },
  { key: "phone", label: "enrollment.InsureeEnrollment.phone", type: "text" },
  { key: "email", label: "enrollment.InsureeEnrollment.email", type: "email" },
  {
    key: "passport",
    label: "enrollment.InsureeEnrollment.passport",
    type: "text",
  },
  {
    key: "current_address",
    label: "enrollment.InsureeEnrollment.currentAddress",
    type: "text",
  },
  {
    key: "geolocation",
    label: "enrollment.InsureeEnrollment.geolocation",
    type: "text",
  },
  {
    key: "current_village",
    label: "enrollment.InsureeEnrollment.currentVillage",
    type: "select",
    // This would be populated with location options
  },
  {
    key: "relationship",
    label: "enrollment.InsureeEnrollment.relationship",
    type: "select",
    options: [
      { value: "1", label: "enrollment.relationship.head" },
      { value: "2", label: "enrollment.relationship.spouse" },
      { value: "3", label: "enrollment.relationship.child" },
      { value: "4", label: "enrollment.relationship.parent" },
      { value: "5", label: "enrollment.relationship.sibling" },
      { value: "6", label: "enrollment.relationship.other" },
    ],
  },
  {
    key: "profession",
    label: "enrollment.InsureeEnrollment.profession",
    type: "select",
    options: [
      { value: "1", label: "enrollment.profession.farmer" },
      { value: "2", label: "enrollment.profession.teacher" },
      { value: "3", label: "enrollment.profession.trader" },
      { value: "4", label: "enrollment.profession.student" },
      { value: "5", label: "enrollment.profession.unemployed" },
      { value: "6", label: "enrollment.profession.other" },
    ],
  },
  {
    key: "education",
    label: "enrollment.InsureeEnrollment.education",
    type: "select",
    options: [
      { value: "1", label: "enrollment.education.none" },
      { value: "2", label: "enrollment.education.primary" },
      { value: "3", label: "enrollment.education.secondary" },
      { value: "4", label: "enrollment.education.tertiary" },
      { value: "5", label: "enrollment.education.university" },
    ],
  },
  {
    key: "type_of_id",
    label: "enrollment.InsureeEnrollment.typeOfId",
    type: "select",
    options: [
      { value: "N", label: "enrollment.identificationType.nationalId" },
      { value: "P", label: "enrollment.identificationType.passport" },
      { value: "D", label: "enrollment.identificationType.drivingLicense" },
      { value: "V", label: "enrollment.identificationType.voterCard" },
      { value: "O", label: "enrollment.identificationType.other" },
    ],
  },
  {
    key: "health_facility",
    label: "enrollment.InsureeEnrollment.healthFacility",
    type: "select",
    // This would be populated with health facility options
  },
  {
    key: "status",
    label: "enrollment.InsureeEnrollment.status",
    type: "select",
    required: true,
    options: [
      { value: "AC", label: "enrollment.status.active" },
      { value: "IN", label: "enrollment.status.inactive" },
      { value: "DE", label: "enrollment.status.dead" },
    ],
  },
  {
    key: "status_date",
    label: "enrollment.InsureeEnrollment.statusDate",
    type: "date",
  },
  {
    key: "status_reason",
    label: "enrollment.InsureeEnrollment.statusReason",
    type: "select",
    // This would be populated with status reason options
  },
  {
    key: "photo",
    label: "enrollment.InsureeEnrollment.photo",
    type: "photo",
  },
  {
    key: "isHead",
    label: "enrollment.InsureeEnrollment.isHead",
    type: "checkbox",
  },
  {
    key: "cardIssued",
    label: "enrollment.InsureeEnrollment.cardIssued",
    type: "checkbox",
  },
  {
    key: "offline",
    label: "enrollment.InsureeEnrollment.offline",
    type: "checkbox",
  },
  {
    key: "validityFrom",
    label: "enrollment.InsureeEnrollment.validityFrom",
    type: "date",
  },
  {
    key: "validityTo",
    label: "enrollment.InsureeEnrollment.validityTo",
    type: "date",
  },
  {
    key: "legacyId",
    label: "enrollment.InsureeEnrollment.legacyId",
    type: "text",
  },
];

// Schema-based field definitions for Family
const FAMILY_FIELDS = [
  {
    key: "headInsuree",
    label: "enrollment.Family.headInsuree",
    type: "text",
  },
  {
    key: "locationId",
    label: "enrollment.Family.locationId",
    type: "select",
  },
  { key: "poverty", label: "enrollment.Family.poverty", type: "checkbox" },
  {
    key: "familyType",
    label: "enrollment.Family.familyType",
    type: "select",
    options: [
      { value: "N", label: "enrollment.familyType.nuclear" },
      { value: "E", label: "enrollment.familyType.extended" },
      { value: "O", label: "enrollment.familyType.other" },
    ],
  },
  {
    key: "address",
    label: "enrollment.Family.address",
    type: "text",
  },
  {
    key: "isOffline",
    label: "enrollment.Family.isOffline",
    type: "checkbox",
  },
  {
    key: "ethnicity",
    label: "enrollment.Family.ethnicity",
    type: "text",
  },
  {
    key: "confirmationNo",
    label: "enrollment.Family.confirmationNo",
    type: "text",
  },
  {
    key: "confirmationType",
    label: "enrollment.Family.confirmationType",
    type: "select",
    options: [
      { value: "S", label: "enrollment.confirmationType.social_worker" },
      { value: "M", label: "enrollment.confirmationType.management" },
      { value: "I", label: "enrollment.confirmationType.insuree" },
    ],
  },
  {
    key: "validityFrom",
    label: "enrollment.Family.validityFrom",
    type: "date",
  },
  { key: "validityTo", label: "enrollment.Family.validityTo", type: "date" },
  { key: "legacyId", label: "enrollment.Family.legacyId", type: "text" },
];

class InsureeEnrollmentForm extends React.Component {
  renderField = (field, value, onChange, intl, classes) => {
    const { key, label, type, required, options } = field;
    const fieldValue = value || "";

    switch (type) {
      case "text":
      case "email":
        return (
          <TextField
            key={key}
            className={classes.field}
            label={formatMessage(intl, "enrollment", label)}
            value={fieldValue}
            onChange={(e) => onChange(key, e.target.value)}
            required={required}
            type={type === "email" ? "email" : "text"}
            fullWidth
            variant="outlined"
            size="small"
          />
        );

      case "date":
        return (
          <TextField
            key={key}
            className={classes.field}
            label={formatMessage(intl, "enrollment", label)}
            value={fieldValue}
            onChange={(e) => onChange(key, e.target.value)}
            required={required}
            type="date"
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        );

      case "select":
        return (
          <FormControl
            key={key}
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
          >
            <InputLabel>{formatMessage(intl, "enrollment", label)}</InputLabel>
            <Select
              value={fieldValue}
              onChange={(e) => onChange(key, e.target.value)}
              required={required}
              label={formatMessage(intl, "enrollment", label)}
            >
              {options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {formatMessage(intl, "enrollment", option.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "checkbox":
        return (
          <FormControlLabel
            key={key}
            className={classes.field}
            control={
              <Checkbox
                checked={Boolean(fieldValue)}
                onChange={(e) => onChange(key, e.target.checked)}
                color="primary"
              />
            }
            label={formatMessage(intl, "enrollment", label)}
          />
        );

      case "photo":
        return (
          <Box key={key} className={classes.field}>
            <Typography variant="body2" gutterBottom>
              {formatMessage(intl, "enrollment", label)}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {fieldValue?.photo && (
                <Avatar
                  src={fieldValue.photo}
                  alt="Insuree Photo"
                  style={{ width: 80, height: 80 }}
                />
              )}
              <Box>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id={`photo-upload-${key}`}
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        onChange(key, {
                          photo: reader.result,
                          date: new Date().toISOString(),
                          officer: "current_user", // This should be replaced with actual user
                        });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <label htmlFor={`photo-upload-${key}`}>
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<PhotoCamera />}
                    size="small"
                  >
                    {fieldValue?.photo ? "Change Photo" : "Upload Photo"}
                  </Button>
                </label>
                {fieldValue?.photo && (
                  <Button
                    variant="text"
                    size="small"
                    color="secondary"
                    onClick={() => onChange(key, null)}
                    style={{ marginLeft: 8 }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Box>
            {fieldValue?.date && (
              <Typography
                variant="caption"
                display="block"
                style={{ marginTop: 4 }}
              >
                Uploaded: {new Date(fieldValue.date).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  renderSection = (title, fields, data, onChange, intl, classes) => {
    return (
      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            {formatMessage(intl, "enrollment", title)}
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          <Grid container spacing={2}>
            {fields.map((field) => (
              <Grid item xs={12} sm={6} md={4} key={field.key}>
                {this.renderField(
                  field,
                  data[field.key],
                  onChange,
                  intl,
                  classes
                )}
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  render() {
    const {
      classes,
      intl,
      insureeData = {},
      familyData = {},
      onInsureeChange,
      onFamilyChange,
      showFamilySection = true,
    } = this.props;

    return (
      <div>
        {/* Insuree Information Section */}
        {this.renderSection(
          "enrollment.InsureeEnrollment.insureeInfo",
          INSUREE_FIELDS,
          insureeData,
          onInsureeChange,
          intl,
          classes
        )}

        {/* Family Information Section */}
        {showFamilySection &&
          this.renderSection(
            "enrollment.InsureeEnrollment.familyInfo",
            FAMILY_FIELDS,
            familyData,
            onFamilyChange,
            intl,
            classes
          )}
      </div>
    );
  }
}

export default withStyles(styles)(InsureeEnrollmentForm);
